<script setup lang="ts">
import { useRoute, useRouter } from '#imports';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import {
  ClipboardCheckIcon,
  ClipboardCopyIcon,
  DotsHorizontalIcon,
  FingerPrintIcon,
  FlagIcon,
  GlobeAltIcon,
  ShareIcon,
} from '@heroicons/vue/outline';
import { BadgeCheckIcon, StarIcon, UserIcon } from '@heroicons/vue/solid';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ProfileActivity from '~/components/profile/ProfileActivity.vue';
import { getApiRoot } from '~/utilities/api';
import { avatarGradientStyles } from '~/utilities/avatar-gradient';
import { BASE_TITLE } from '~/utilities/constants';

definePageMeta({
  backgroundClass: 'bg-neutral-100 dark:bg-neutral-800',
  noPadding: true,
  whiteNavbar: true,
});

const route = useRoute();
const router = useRouter();

const { t } = useI18n();

const props = defineProps({ isEmbedded: Boolean });
const { isEmbedded } = toRefs(props);

const username = route.params?.username;
let id = route.params?.id;
if (id && id.includes('-')) {
  id = id.split('-').pop();
}

const { data: profile } = await useFetch<any>(`${getApiRoot()}/profile/${username || id}?include_counts=True`);
const name = computed(() => profile.value?.name || profile.value?.encoded_id.substring(0, 24));

const meta = [
  { property: 'og:title', content: `${name.value || 'Unknown'} - Profile ${BASE_TITLE}` },
  { name: 'twitter:title', content: `${name.value || 'Unknown'} - Profile ${BASE_TITLE}` },
  { name: 'twitter:card', content: 'summary_large_image' },
];
if (profile.value?.avatar_uri) {
  meta.push({ property: 'og:image', content: profile.value.avatar_uri });
  meta.push({ name: 'twitter:image', content: profile.value.avatar_uri });
}

const userProfiles = useState<any[]>('userProfiles', () => []);

const isMyProfile = computed(() => {
  return profile.value && userProfiles.value?.length > 0 && userProfiles.value.some((p) => p.id === profile.value?.id);
});

useHead({
  title: `${name.value || 'Unknown'} - Profile ${BASE_TITLE}`,
  meta,
} as any);

const showReportModal = ref(false);

const tabs = computed(() => {
  const allTabs = [
    {
      id: 'collections',
      title: 'profile.collections',
      count: profile.value?.collections_count || 0,
    },
    {
      id: 'created',
      title: 'profile.created',
      count: profile.value?.created_nfts_count || 0,
    },
    {
      id: 'owned',
      title: 'profile.owned',
      count: profile.value?.owned_nfts_count || 0,
    },
    {
      id: 'activity',
      title: 'profile.activity',
    },
  ];
  if (profile.value.active_subscription) {
    allTabs.unshift({
      id: 'featured',
      title: 'profile.featured',
      count: profile.value?.featured_nfts_count || 0,
    });
  }
  if (true) {
    // TODO
    allTabs.unshift({
      id: 'mints',
      title: 'Mints',
      count: profile.value.active_mints_count,
    });
  }
  return allTabs;
});

const defaultTab =
  profile.value?.active_mints_count > 0
    ? 'mints'
    : profile.value?.featured_nfts_count > 0
    ? 'featured'
    : profile.value?.owned_nfts_count > 0 && profile.value?.created_nfts_count === 0
    ? 'owned'
    : 'collections';

const selectedTab = ref(route.query.tab ? route.query.tab : defaultTab);
watch(selectedTab, (tab) => {
  router.replace({
    path: route.path,
    query: { tab },
  });
});

const copied = ref(false);
const copyToClipboard = (input: string) => {
  navigator.clipboard.writeText(input);
  copied.value = true;
  setTimeout(() => (copied.value = false), 1000);
};

const twitterUrl = ref('');
if (process.client && profile.value) {
  twitterUrl.value = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
    profile.value.twitter_handle
      ? t('profile.shareWithHandle', { handle: profile.value.twitter_handle, url: window.location.href })
      : t('profile.shareWithoutHandle', { url: window.location.href })
  )}`;
}

const gridStyle = useCookie<'grid-compact' | 'grid' | 'table'>('grid-style');
gridStyle.value = gridStyle.value || 'grid';
</script>

<template>
  <div class="flex flex-grow flex-col">
    <div v-if="!profile">
      <div class="mb-16 h-[7.2rem] bg-neutral-800"></div>
      <Error404 />
    </div>
    <template v-else>
      <div class="flex flex-col">
        <div
          :class="'grid-rows-[8rem,4rem,4rem,auto] sm:grid-rows-[10rem,6rem,6rem,auto] lg:grid-rows-[14rem,6rem,6rem,auto]'"
          class="grid bg-white dark:bg-black"
        >
          <div
            :class="profile.header_uri ? 'bg-black dark:bg-white' : 'bg-header'"
            class="col-start-1 row-start-1 row-end-3"
          >
            <img
              v-if="profile.header_uri"
              :src="profile.header_uri"
              class="h-full w-full object-cover object-center opacity-80"
            />
          </div>
          <Container :class="'justify-center'" class="col-start-1 row-start-2 row-end-4 flex">
            <div
              :class="[
                'z-10 h-36 w-36 rounded-full ring-8 ring-offset-4 sm:h-48 sm:w-48',
                profile.active_subscription
                  ? 'ring-white ring-offset-amber-500 dark:ring-black dark:ring-offset-amber-500'
                  : 'ring-white ring-offset-white dark:ring-black dark:ring-offset-black',
                !profile.avatar_uri ? 'flex items-center justify-center bg-gradient-to-br' : 'bg-white dark:bg-black',
              ]"
              :style="avatarGradientStyles(profile.id)"
            >
              <img
                v-if="profile.avatar_uri"
                :src="profile.avatar_uri"
                class="inline h-full w-full rounded-full object-cover"
              />
              <FingerPrintIcon v-else class="h-24 w-24 text-neutral-200 dark:text-neutral-700" />
            </div>
          </Container>
          <Container class="col-start-1 row-start-4 mt-8 flex flex-col justify-center gap-4 md:flex-row">
            <div class="flex flex-col items-center gap-4">
              <h1
                v-if="profile.name"
                class="flex items-center gap-1 text-xl font-bold text-neutral-900 dark:text-neutral-50 md:text-3xl"
              >
                {{ profile.name }}
                <BadgeCheckIcon v-if="profile.verification_state === 1" class="h-8 w-8 text-emerald-600" />
              </h1>
              <div class="flex flex-col flex-wrap items-center justify-center gap-4 py-1 md:flex-row">
                <div class="flex">
                  <div
                    class="group flex cursor-pointer items-center justify-center overflow-hidden rounded-full border pr-2 text-sm font-medium transition duration-200 ease-in-out"
                    @click="copyToClipboard(profile.encoded_id)"
                  >
                    <div class="rounded-full bg-emerald-700 px-3 py-2 font-mono tracking-tight text-white">
                      did:chia
                    </div>
                    <span class="py-2 pl-1 font-mono tracking-tight text-neutral-500 dark:text-neutral-400"
                      >{{ profile.encoded_id.replace('did:chia:', '').substring(0, 6) }}...{{
                        profile.encoded_id.substring(profile.encoded_id.length - 6)
                      }}</span
                    >
                    <div class="mb-[1px] ml-1 flex">
                      <ClipboardCheckIcon v-if="copied" class="h-6 w-6 text-emerald-600" />
                      <ClipboardCopyIcon
                        v-else
                        class="ml-[1px] h-6 w-6 text-neutral-300 group-hover:text-neutral-600 dark:text-neutral-300 dark:group-hover:text-neutral-300"
                      />
                    </div>
                  </div>
                </div>
                <div class="flex gap-4 py-1">
                  <div v-if="profile.active_subscription">
                    <div
                      class="group flex items-center justify-center gap-1 overflow-hidden rounded-full border border-amber-500 px-2 py-2 text-sm font-medium shadow-sm ring-1 ring-amber-500 md:px-4"
                    >
                      <StarIcon class="h-6 w-6 text-amber-500" />
                      <span class="hidden font-bold text-amber-500 md:block">Premium</span>
                    </div>
                  </div>
                  <div v-if="isMyProfile">
                    <div
                      class="group flex items-center justify-center gap-1 overflow-hidden rounded-full border border-sky-500 px-2 py-2 text-sm font-medium shadow-sm ring-1 ring-sky-500 md:px-4"
                    >
                      <UserIcon class="h-6 w-6 text-sky-500" />
                      <span class="hidden font-bold text-sky-500 md:block">Your Profile</span>
                    </div>
                  </div>
                  <div v-if="profile.website">
                    <NuxtLink
                      :to="profile.website"
                      target="_blank"
                      class="group flex cursor-pointer items-center justify-center gap-1 overflow-hidden rounded-full border px-2 py-2 text-sm font-medium shadow-sm duration-100 ease-in-out hover:border-black hover:ring-1 hover:ring-black dark:hover:border-white dark:hover:ring-white md:px-4"
                    >
                      <GlobeAltIcon class="h-6 w-6 text-neutral-500 dark:text-neutral-400" />
                      <span class="hidden text-neutral-500 dark:text-neutral-400 md:block">{{
                        profile.website.replace('https://', '').replace('www.', '')
                      }}</span>
                    </NuxtLink>
                  </div>
                  <div v-if="profile.twitter_handle">
                    <NuxtLink
                      :to="`https://twitter.com/${profile.twitter_handle}`"
                      target="_blank"
                      class="group flex cursor-pointer items-center justify-center gap-1 overflow-hidden rounded-full border px-2 py-2 text-sm font-medium shadow-sm duration-100 ease-in-out hover:border-black hover:ring-1 hover:ring-black dark:hover:border-white dark:hover:ring-white md:px-4"
                    >
                      <span
                        ><svg viewBox="0 0 300 271" class="h-6 w-6 dark:fill-neutral-400" aria-hidden="true">
                          <path
                            d="m236 0h46l-101 115 118 156h-92.6l-72.5-94.8-83 94.8h-46l107-123-113-148h94.9l65.5 86.6zm-16.1 244h25.5l-165-218h-27.4z"
                          /></svg
                      ></span>
                      <span class="hidden text-neutral-500 dark:text-neutral-400 md:block"
                        >@{{ profile.twitter_handle }}</span
                      >
                    </NuxtLink>
                  </div>
                  <!--                  <div v-if="profile.discord_user">-->
                  <!--                    <NuxtLink-->
                  <!--                      :to="`https://discordapp.com/users/${profile.discord_user.id}`"-->
                  <!--                      target="_blank"-->
                  <!--                      class="group cursor-pointer overflow-hidden border rounded-full py-2 px-2 md:px-4 flex items-center justify-center gap-1 text-sm font-medium shadow-sm hover:ring-1 hover:ring-black dark:hover:ring-white hover:border-black dark:hover:border-white ease-in-out duration-100"-->
                  <!--                    >-->
                  <!--                      <span class="text-indigo-500">-->
                  <!--                        <svg fill="currentColor" viewBox="0 0 71 55" class="h-6 w-6" aria-hidden="true">-->
                  <!--                          <path-->
                  <!--                            fill-rule="evenodd"-->
                  <!--                            clip-rule="evenodd"-->
                  <!--                            d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.435C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6019 45.5576C70.6551 45.5182 70.6887 45.459 70.6943 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1066 30.1693C30.1066 34.1136 27.28 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.9371 34.1136 40.9371 30.1693C40.9371 26.225 43.7636 23.0133 47.3178 23.0133C50.9 23.0133 53.7545 26.2532 53.6986 30.1693C53.6986 34.1136 50.9 37.3253 47.3178 37.3253Z"-->
                  <!--                          ></path></svg-->
                  <!--                      ></span>-->
                  <!--                      <span class="pl-1 hidden md:block text-neutral-500 dark:text-neutral-400">{{-->
                  <!--                        profile.discord_user.username + '#' + profile.discord_user.discriminator-->
                  <!--                      }}</span>-->
                  <!--                    </NuxtLink>-->
                  <!--                  </div>-->
                  <div>
                    <NuxtLink
                      v-if="twitterUrl"
                      rel="canonical"
                      :to="twitterUrl"
                      target="_blank"
                      class="group flex cursor-pointer items-center justify-center gap-1 overflow-hidden rounded-full border px-2 py-2 text-sm font-medium shadow-sm duration-100 ease-in-out hover:border-black hover:ring-1 hover:ring-black dark:hover:border-white dark:hover:ring-white md:px-4"
                    >
                      <ShareIcon class="h-6 w-6 text-neutral-400 group-hover:text-neutral-500 dark:text-neutral-400" />
                      <span class="hidden text-neutral-500 dark:text-neutral-400 md:block">{{
                        $t('profile.share')
                      }}</span>
                    </NuxtLink>
                    <div
                      v-else
                      class="group flex cursor-pointer items-center justify-center gap-1 overflow-hidden rounded-full border px-2 py-2 text-sm font-medium shadow-sm duration-100 ease-in-out hover:border-black hover:ring-1 hover:ring-black dark:hover:border-white dark:hover:ring-white md:px-4"
                    >
                      <ShareIcon class="h-6 w-6 text-neutral-400 group-hover:text-neutral-500 dark:text-neutral-400" />
                      <span class="hidden text-neutral-500 dark:text-neutral-400 md:block">{{
                        $t('profile.share')
                      }}</span>
                    </div>
                  </div>
                  <div class="flex">
                    <Menu as="div" class="relative z-10 inline-block text-left">
                      <div>
                        <MenuButton
                          class="inline-flex w-full justify-center rounded-full border p-[9px] text-sm font-medium text-neutral-700 shadow-sm duration-100 ease-in-out hover:border-black hover:ring-1 hover:ring-black dark:text-neutral-200 dark:hover:border-white dark:hover:ring-white"
                        >
                          <DotsHorizontalIcon class="h-5 w-5" />
                        </MenuButton>
                      </div>

                      <transition
                        enter-active-class="transition ease-out duration-100"
                        enter-from-class="transform opacity-0 scale-95"
                        enter-to-class="transform opacity-100 scale-100"
                        leave-active-class="transition ease-in duration-75"
                        leave-from-class="transform opacity-100 scale-100"
                        leave-to-class="transform opacity-0 scale-95"
                      >
                        <MenuItems
                          class="absolute right-0 mt-2 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none dark:bg-black"
                        >
                          <div class="py-1">
                            <MenuItem v-slot="{ active }">
                              <a
                                href="#"
                                :class="[
                                  active
                                    ? 'bg-neutral-100 text-neutral-900 dark:bg-neutral-800 dark:text-neutral-50'
                                    : 'text-neutral-700 dark:text-neutral-200',
                                  'group flex items-center px-4 py-2 text-sm',
                                ]"
                                @click="showReportModal = true"
                              >
                                <FlagIcon
                                  class="mr-3 h-5 w-5 text-neutral-400 group-hover:text-neutral-500 dark:text-neutral-400"
                                  aria-hidden="true"
                                />
                                {{ $t('profile.report') }}
                              </a>
                            </MenuItem>
                          </div>
                        </MenuItems>
                      </transition>
                    </Menu>
                  </div>
                </div>
              </div>
              <p
                v-if="profile.bio"
                class="break-words-properly mt-2 max-h-[15rem] overflow-auto whitespace-pre-wrap text-center text-sm font-medium text-neutral-700 dark:text-neutral-200"
              >
                {{ profile.bio }}
              </p>
            </div>
          </Container>
        </div>
      </div>
      <div class="w-full flex-grow bg-white pt-8 dark:bg-black">
        <Container>
          <div>
            <div>
              <Tabs v-model="selectedTab" :tabs="tabs">
                <div v-if="selectedTab !== 'collections'" class="flex h-full items-center px-3">
                  <ExploreGridToggle v-model="gridStyle" />
                </div>
              </Tabs>
            </div>
            <div class="py-8">
              <ProfileMints v-if="selectedTab === 'mints'" :profile="profile" />
              <ProfileFeatured
                v-if="profile.active_subscription && selectedTab === 'featured'"
                :profile="profile"
                :grid-style="gridStyle"
                :editable="isMyProfile"
              />
              <ProfileNfts
                v-if="selectedTab === 'created'"
                :profile="profile"
                :type="selectedTab"
                :grid-style="gridStyle"
              />
              <ProfileNfts
                v-else-if="selectedTab === 'owned'"
                :profile="profile"
                :type="selectedTab"
                :grid-style="gridStyle"
                :show-creator="true"
              />
              <ProfileCollections v-else-if="selectedTab === 'collections'" :profile="profile" />
              <ProfileActivity v-if="selectedTab === 'activity'" :profile="profile" />
            </div>
          </div>
        </Container>
      </div>
    </template>
    <ReportModal v-if="profile" v-model="showReportModal" :profile-id="profile.id" />
  </div>
</template>

<style scoped>
.bg-header {
  background-color: #262626;
  background-image: url('/bg-header.svg');
}
</style>
